/**
 * ResourceTypeModal Component
 * Modal for creating and editing ResourceType entries
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Autocomplete,
  Typography,
  FormHelperText,
} from '@mui/material';
import AdminButton from 'components/common/AdminButton';

/**
 * Modal for creating or editing a ResourceType
 * @param {boolean} open - Whether modal is open
 * @param {function} onClose - Handler for closing modal
 * @param {function} onSave - Handler for saving (create/update)
 * @param {Object|null} resourceType - Existing resource type for editing (null for create)
 * @param {boolean} isSaving - Whether save operation is in progress
 * @param {Error|null} saveError - Error from save operation
 */
function ResourceTypeModal({ open, onClose, onSave, resourceType, isSaving, saveError }) {
  const isEditMode = !!resourceType;

  const [formData, setFormData] = useState({
    name: '',
    resourcePrefix: '',
    icon: '',
    description: '',
    status: 'ACTIVE',
    domainRestrictions: [],
  });

  const [validationErrors, setValidationErrors] = useState({});

  // Available domain options (from GraphQL Group enum)
  const domainOptions = [
    'OPERATIONS',
    'FINANCE',
    'HUMAN_RESOURCES',
    'INFORMATION_TECHNOLOGY',
    'MARKETING',
    'EXECUTIVE',
  ];

  // Reset form when modal opens or resourceType changes
  useEffect(() => {
    if (open) {
      if (resourceType) {
        // Edit mode - populate with existing data
        setFormData({
          name: resourceType.name || '',
          resourcePrefix: resourceType.resourcePrefix || '',
          icon: resourceType.icon || '',
          description: resourceType.description || '',
          status: resourceType.status || 'ACTIVE',
          domainRestrictions: resourceType.domainRestrictions || [],
        });
      } else {
        // Create mode - reset to defaults
        setFormData({
          name: '',
          resourcePrefix: '',
          icon: '',
          description: '',
          status: 'ACTIVE',
          domainRestrictions: [],
        });
      }
      setValidationErrors({});
    }
  }, [open, resourceType]);

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate form data
  const validate = () => {
    const errors = {};

    // Name is required
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    // Resource prefix is required and must be a single letter
    if (!formData.resourcePrefix.trim()) {
      errors.resourcePrefix = 'Resource prefix is required';
    } else if (!/^[A-Z]$/.test(formData.resourcePrefix.trim())) {
      errors.resourcePrefix = 'Resource prefix must be a single uppercase letter (A-Z)';
    }

    // Status is required
    if (!formData.status) {
      errors.status = 'Status is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    // Prepare data for save
    const dataToSave = {
      name: formData.name.trim(),
      resourcePrefix: formData.resourcePrefix.trim().toUpperCase(),
      icon: formData.icon.trim() || null,
      description: formData.description.trim() || null,
      status: formData.status,
      domainRestrictions: formData.domainRestrictions.length > 0 ? formData.domainRestrictions : null,
    };

    // Add id for edit mode
    if (isEditMode) {
      dataToSave.id = resourceType.id;
    }

    await onSave(dataToSave);
  };

  // Handle cancel
  const handleCancel = () => {
    setValidationErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight="bold">
          {isEditMode ? 'Edit Resource Type' : 'Create Resource Type'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {isEditMode
            ? 'Update the resource type information below.'
            : 'Add a new resource type to the system.'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError.message || 'Failed to save resource type. Please try again.'}
          </Alert>
        )}

        <Stack spacing={2.5}>
          {/* Name Field */}
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={!!validationErrors.name}
            helperText={validationErrors.name || 'Display name for the resource type (e.g., "Reports", "Admin")'}
            fullWidth
            required
            disabled={isSaving}
            autoFocus
          />

          {/* Resource Prefix Field */}
          <TextField
            label="Resource Prefix"
            value={formData.resourcePrefix}
            onChange={(e) => handleChange('resourcePrefix', e.target.value.toUpperCase())}
            error={!!validationErrors.resourcePrefix}
            helperText={
              validationErrors.resourcePrefix ||
              'Single letter used as the first character of resource codes (e.g., "R" for R0001, "A" for A0001)'
            }
            fullWidth
            required
            disabled={isSaving}
            inputProps={{
              maxLength: 1,
              style: { textTransform: 'uppercase' },
            }}
          />

          {/* Icon Field */}
          <TextField
            label="Icon"
            value={formData.icon}
            onChange={(e) => handleChange('icon', e.target.value)}
            helperText="Material-UI icon name (optional)"
            fullWidth
            disabled={isSaving}
          />

          {/* Description Field */}
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            helperText="Brief description of the resource type (optional)"
            fullWidth
            multiline
            rows={3}
            disabled={isSaving}
          />

          {/* Status Field */}
          <FormControl fullWidth required error={!!validationErrors.status} disabled={isSaving}>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              label="Status"
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </Select>
            <FormHelperText>
              {validationErrors.status || 'Whether this resource type is currently active'}
            </FormHelperText>
          </FormControl>

          {/* Domain Restrictions Field */}
          <Box>
            <Autocomplete
              multiple
              options={domainOptions}
              value={formData.domainRestrictions}
              onChange={(e, newValue) => handleChange('domainRestrictions', newValue)}
              disabled={isSaving}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Domain Restrictions"
                  helperText="Limit which domains can access this resource type (leave empty for all domains)"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))
              }
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <AdminButton onClick={handleCancel} disabled={isSaving} variant="outlined">
          Cancel
        </AdminButton>
        <AdminButton
          onClick={handleSave}
          disabled={isSaving}
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={16} /> : null}
        >
          {isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
        </AdminButton>
      </DialogActions>
    </Dialog>
  );
}

export default ResourceTypeModal;
